/**
 * The public calculator, one question at a time: the host country, the title,
 * its dates, the trips — then the answer, which is the dashboard's (AC-1).
 *
 * One react-hook-form holds the title (`inoutTitleFormSchema`) across its two
 * steps; each step checks only its own fields. Changing an answer drops the
 * result, so a result on screen always reads the answers above it.
 *
 * "The rest of the time I was in the country" is what the dashboard learns
 * from presence confirmations: said here, it is one confirmed period from the
 * title's issue to today, and the days between the trips stop counting as
 * maybe away. Unticked, they stay uncertain — and the answer says so, with the
 * way to count them in one click.
 *
 * "What if I travel?" simulates one trip on the same record, nothing saved.
 *
 * "Keep" hands the record and the answer to the host (`onKeep`) — the wizard
 * does not know where it runs: the public page, Citizen Web, the app or a
 * partner's portal. A calculation kept before an account exists travels by a
 * hand-off the account adopts; it never reopens the wizard.
 */
import { zodResolver } from '@hookform/resolvers/zod';
import { useAnalytics } from '@tonnie/observability';
import { inoutTitleFormSchema, type InOutTitleFormInput } from '@tonnie/schemas';
import {
  useInOutCalculatorEvaluate,
  useInOutCalculatorRegimes,
  useInOutCalculatorSimulate,
  type InOutReading,
} from '@tonnie/sdk';
import { useState } from 'react';
import { useForm } from 'react-hook-form';

import { inoutAnswerSummary, inoutJoinedTrip } from '@tonnie/heroes';

import type { InOutKeptCalculation, TripRow } from '../lib/kept-calculation';
import { deviceToday } from '../lib/local-today';
import { useInOutRefusalText } from './use-inout-refusal-text';

export const CALCULATOR_STEPS = ['country', 'title', 'dates', 'trips', 'result'] as const;
export type CalculatorStep = (typeof CALCULATOR_STEPS)[number];

const EMPTY_TITLE: InOutTitleFormInput = { regime: '', subtype: '', issuedOn: '', validUntil: '' };

/** A trip the person might take — what "what if I travel?" simulates. */
export interface CalculatorTrip {
  departsOn: string;
  returnsOn: string;
  destinationCountry: string | null;
}

export interface CalculatorWizardOptions {
  /** The country already chosen (a portal's own). */
  startCountry?: string | null;
  /** What keeping the answer does — the host's. */
  onKeep: (kept: InOutKeptCalculation) => void;
}

export function useCalculatorWizard({ startCountry = null, onKeep }: CalculatorWizardOptions) {
  const { track } = useAnalytics();
  const refusalText = useInOutRefusalText();

  const [step, setStep] = useState<CalculatorStep>('country');
  const [country, setCountryState] = useState(startCountry ?? '');
  const [trips, setTrips] = useState<TripRow[]>([]);
  const [restInHost, setRestInHostState] = useState(true);
  const [answer, setAnswer] = useState<InOutReading | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [tripProblem, setTripProblem] = useState<string | null>(null);

  const form = useForm<InOutTitleFormInput>({
    resolver: zodResolver(inoutTitleFormSchema),
    defaultValues: EMPTY_TITLE,
    mode: 'onTouched',
  });

  const regimes = useInOutCalculatorRegimes(country);
  const evaluate = useInOutCalculatorEvaluate();
  const simulate = useInOutCalculatorSimulate();

  const dropAnswer = () => {
    setAnswer(null);
    setProblem(null);
    setTripProblem(null);
    simulate.reset();
  };

  /** The record the answers describe — what the calculator sends, and what "keep" carries on. */
  const record = (inHost = restInHost) => {
    const title = form.getValues();
    const today = deviceToday();
    return {
      countryCode: country,
      title: {
        regime: title.regime,
        subtype: title.subtype || null,
        issuedOn: title.issuedOn,
        validUntil: title.validUntil || null,
      },
      absences: trips.map(({ departedOn, returnedOn, destinationCountry }) => ({
        departedOn,
        returnedOn,
        destinationCountry,
      })),
      presentPeriods:
        inHost && title.issuedOn && title.issuedOn <= today
          ? [{ periodStart: title.issuedOn, periodEnd: today }]
          : [],
      today,
    };
  };

  const calculate = (inHost = restInHost) => {
    setProblem(null);
    evaluate.mutate(record(inHost), {
      onSuccess: (result) => {
        track('INOUT_CALCULATOR_EVALUATED', { trips: trips.length, rest_in_host: inHost });
        setAnswer(result);
        setStep('result');
      },
      onError: (error) => {
        setAnswer(null);
        setProblem(refusalText(error));
      },
    });
  };

  /** Next from a step: only once its own answers are valid. */
  const next = async () => {
    switch (step) {
      case 'country':
        if (country) setStep('title');
        return;
      case 'title':
        if (await form.trigger(['regime', 'subtype'])) setStep('dates');
        return;
      case 'dates':
        if (await form.trigger(['issuedOn', 'validUntil'])) setStep('trips');
        return;
      case 'trips':
        calculate();
        return;
      case 'result':
        return;
      default: {
        const unreachable: never = step;
        throw new Error(`Unknown calculator step: ${String(unreachable)}`);
      }
    }
  };

  const back = () => {
    const at = CALCULATOR_STEPS.indexOf(step);
    if (at > 0) setStep(CALCULATOR_STEPS[at - 1]);
  };

  const keep = () => {
    if (!answer) return;
    const { today: _today, ...described } = record();
    onKeep({
      ...described,
      // The answer the person read at the top — said back in the same words.
      summary: {
        countryCode: described.countryCode,
        trips: trips.length,
        answer: inoutAnswerSummary(answer.evaluation),
      },
    });
  };

  const whatIf = (trip: CalculatorTrip) => {
    setTripProblem(null);
    simulate.mutate(
      { ...record(), trip },
      {
        onSuccess: (result) =>
          track('INOUT_CALCULATOR_SIMULATED', {
            during_renewal_window: result.duringRenewalWindow,
            outlasts_title: result.outlastsTitle,
          }),
        onError: (error) => setTripProblem(refusalText(error)),
      }
    );
  };

  const restart = () => {
    setCountryState('');
    form.reset(EMPTY_TITLE);
    setTrips([]);
    setRestInHostState(true);
    dropAnswer();
    setStep('country');
  };

  return {
    step,
    /** Something answered that closing would lose: past the first question, or a trip. */
    hasAnswers: step !== 'country' || trips.length > 0,
    goTo: setStep,
    next,
    back,
    form,
    country,
    setCountry: (code: string) => {
      setCountryState(code);
      form.reset(EMPTY_TITLE);
      dropAnswer();
    },
    regimes,
    trips,
    addTrip: (trip: TripRow) => {
      setTrips((prev) => [...prev, trip]);
      dropAnswer();
    },
    updateTrip: (trip: TripRow) => {
      setTrips((prev) => prev.map((t) => (t.key === trip.key ? trip : t)));
      dropAnswer();
    },
    /**
     * A trip that shares only a border day with one of the list, made one with
     * it (owner, 22 Oct 2026): the trip kept runs from the first departure to
     * the last return, its destination its own or else the one written; the
     * written trip, when it was already on the list, goes.
     */
    joinTrips: (trip: TripRow, intoKey: string) => {
      setTrips((prev) =>
        prev
          .filter((t) => t.key !== trip.key)
          .map((t) =>
            t.key === intoKey
              ? {
                  ...t,
                  ...inoutJoinedTrip(t, trip),
                  destinationCountry: t.destinationCountry ?? trip.destinationCountry,
                }
              : t
          )
      );
      dropAnswer();
    },
    removeTrip: (key: string) => {
      setTrips((prev) => prev.filter((t) => t.key !== key));
      dropAnswer();
    },
    restInHost,
    setRestInHost: (value: boolean) => {
      setRestInHostState(value);
      dropAnswer();
    },
    onTitleChanged: dropAnswer,
    calculating: evaluate.isPending,
    answer,
    problem,
    /** Count the days between the trips as in the host country, and read the answer again. */
    countRestInHost: () => {
      setRestInHostState(true);
      setTripProblem(null);
      simulate.reset();
      calculate(true);
    },
    keep,
    whatIf,
    simulation: simulate.data ?? null,
    simulating: simulate.isPending,
    tripProblem,
    restart,
  };
}

export type CalculatorWizard = ReturnType<typeof useCalculatorWizard>;
